import React from 'react';
import { Info } from 'lucide-react';
import MuiTooltip from '@mui/material/Tooltip';
import { useManufacturerNamePrecedenceConfig } from '../../../services/api/domains/manufacturers/hooks';
import { ManufacturerNamePrecedenceStep } from '../../../services/api/domains/manufacturers/types';
import { GRAY_400 } from 'theme/grayScale';

const FALLBACK_TOOLTIP_TEXT =
  'Manufacturer Name is resolved using a configured precedence order.';

/**
 * Pure content builder, exported separately so tests can cover every status
 * (idle/loading/success/error) without mocking the fetch hook each time.
 * Mirrors buildPrecedenceTooltipContent (products' PrecedenceOrderTooltip).
 */
export function buildManufacturerPrecedenceTooltipContent(
  status: 'idle' | 'loading' | 'success' | 'error',
  steps: ManufacturerNamePrecedenceStep[] | undefined
): React.ReactNode {
  if (status !== 'success' || !steps || steps.length === 0) {
    return FALLBACK_TOOLTIP_TEXT;
  }

  return (
    <div>
      <div>Manufacturer Name is resolved in order:</div>
      <ol style={{ margin: '4px 0 0', paddingLeft: '18px' }}>
        {steps.map(s => (
          <li key={s.step}>{s.label}</li>
        ))}
      </ol>
    </div>
  );
}

interface ManufacturerPrecedenceOrderTooltipProps {
  /** Icon size in px — callers place this inline next to differently-sized text. */
  size?: number;
}

/**
 * Info icon + hover tooltip showing the live
 * dbo.Manufacturer_Name_Precedence_Config order (issue #653, Task 10,
 * Sub-task 10.1 AC: "Precedence tooltip present").
 */
const ManufacturerPrecedenceOrderTooltip: React.FC<
  ManufacturerPrecedenceOrderTooltipProps
> = ({ size = 13 }) => {
  const result = useManufacturerNamePrecedenceConfig();
  const content = buildManufacturerPrecedenceTooltipContent(
    result.status,
    result.status === 'success' ? result.data : undefined
  );

  return (
    <MuiTooltip
      title={content}
      placement="right"
      arrow
      slotProps={{ popper: { style: { zIndex: 10001 } } }}>
      <button
        type="button"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          color: GRAY_400,
          cursor: 'help',
          background: 'none',
          border: 'none',
          padding: 0,
          margin: 0,
        }}
        aria-label="How is Manufacturer Name resolved?">
        <Info size={size} />
      </button>
    </MuiTooltip>
  );
};

export default ManufacturerPrecedenceOrderTooltip;
